import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trainingConfigService } from '@/services/trainingConfigService';

const trainingConfigKeys = {
  all: ['trainingConfigs'],
  lists: () => [...trainingConfigKeys.all, 'list'],
  list: (type: string | null) => [...trainingConfigKeys.lists(), { type }],
  details: () => [...trainingConfigKeys.all, 'detail'],
  detail: (id: string | number) => [...trainingConfigKeys.details(), id],
  byType: (type: string) => [...trainingConfigKeys.all, 'type', type],
};

export function useTrainingConfigs(type: string | null = null) {
  return useQuery({
    queryKey: trainingConfigKeys.list(type),
    queryFn: () => trainingConfigService.list(type),
  });
}

export function useTrainingConfigsByType(type: string | undefined) {
  return useQuery({
    queryKey: trainingConfigKeys.byType(type ?? ''),
    queryFn: () => trainingConfigService.getByType(type!),
    enabled: !!type,
  });
}

export function useTrainingConfig(id: string | number | undefined) {
  return useQuery({
    queryKey: trainingConfigKeys.detail(id!),
    queryFn: () => trainingConfigService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateTrainingConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configData: Record<string, unknown>) => trainingConfigService.create(configData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingConfigKeys.all });
    },
  });
}

export function useUpdateTrainingConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: Record<string, unknown> }) =>
      trainingConfigService.update(id, data),
    onSuccess: (data: { id?: string | number; type?: string }) => {
      queryClient.invalidateQueries({ queryKey: trainingConfigKeys.all });
      if (data.id) queryClient.invalidateQueries({ queryKey: trainingConfigKeys.detail(data.id) });
      if (data.type) queryClient.invalidateQueries({ queryKey: trainingConfigKeys.byType(data.type) });
    },
  });
}

export function useDeleteTrainingConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => trainingConfigService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingConfigKeys.all });
    },
  });
}
